import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Server-side liste yönetimi hook'u.
 * - Sayfalama, arama (300ms debounce), sıralama, filtre desteği
 * - Otomatik abort/refetch
 *
 * Kullanım:
 *   const list = useServerList('/pk/ekipmanlar', {
 *     pageSize: 100,
 *     initialSort: 'created_at:desc',
 *     initialFilters: { aktif: true },
 *   });
 *   list.items, list.total, list.page, list.totalPages, list.loading
 *   list.setSearch(...), list.setSort(...), list.setFilter(name, value), list.setPage(n)
 *   list.refetch()
 */
export function useServerList(endpoint, opts = {}) {
  const {
    pageSize: initialPageSize = 100,
    initialSort = 'created_at:desc',
    initialFilters = {},
    debounceMs = 300,
    autoFetch = true,
  } = opts;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [search, setSearchState] = useState('');
  const [sort, setSort] = useState(initialSort);
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const fetchSeq = useRef(0);

  const buildParams = useCallback(() => {
    const params = { page, page_size: pageSize, sort, paginate: 1 };
    if (search && search.trim()) params.search = search.trim();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) return;
      params[`f_${k}`] = Array.isArray(v) ? v.join(',') : v;
    });
    return params;
  }, [page, pageSize, sort, search, filters]);

  const doFetch = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const mySeq = ++fetchSeq.current;

    setLoading(true);
    setError(null);
    try {
      const params = buildParams();
      const res = await axios.get(`${API}${endpoint}`, {
        params, withCredentials: true, signal: controller.signal,
      });
      // Sadece son istek dikkate alınır
      if (mySeq !== fetchSeq.current) return;
      const data = res.data || {};
      // Eski API'lara geri uyumluluk (sadece array dönenler için)
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
        setTotalPages(1);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 0);
      }
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      setError(err);
      setItems([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      if (mySeq === fetchSeq.current) setLoading(false);
    }
  }, [endpoint, buildParams]);

  // İlk yükleme ve sayfalama/sort/filter değişimi → anında fetch
  useEffect(() => {
    if (!autoFetch) return;
    doFetch();
  }, [endpoint, page, pageSize, sort, JSON.stringify(filters)]);// eslint-disable-line

  // search değişiminde debounce
  useEffect(() => {
    if (!autoFetch) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Arama değiştiğinde sayfayı 1'e al
      setPage(1);
      doFetch();
    }, debounceMs);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search]);// eslint-disable-line

  const setSearch = (v) => setSearchState(v);
  const setFilter = (key, value) => setFilters(prev => {
    const next = { ...prev, [key]: value };
    return next;
  });
  const setFiltersBulk = (obj) => setFilters(obj);
  const resetFilters = () => { setFilters(initialFilters); setSearchState(''); setPage(1); };

  // Sayfa/Sort/Filter değiştiğinde page reset için yardımcı
  useEffect(() => { setPage(1); }, [sort, JSON.stringify(filters), pageSize]);

  /**
   * Mevcut filtre/arama/sort ile TÜM eşleşen kayıtları getir (export için).
   * Sayfalama uygulanmaz. Backend `all=1` parametresini desteklemelidir.
   */
  const fetchAll = useCallback(async () => {
    const params = { sort, all: 1 };
    if (search && search.trim()) params.search = search.trim();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) return;
      params[`f_${k}`] = Array.isArray(v) ? v.join(',') : v;
    });
    const res = await axios.get(`${API}${endpoint}`, { params, withCredentials: true });
    const data = res.data || {};
    if (Array.isArray(data)) return data;
    return data.items || [];
  }, [endpoint, sort, search, filters]);

  return {
    items, total, totalPages, page, pageSize, search, sort, filters, loading, error,
    setPage, setPageSize, setSearch, setSort, setFilter, setFilters: setFiltersBulk,
    resetFilters, refetch: doFetch, fetchAll,
  };
}
